import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, ExternalLink, Plus, Search } from "lucide-react";
import CopyButton from "../components/CopyButton";
import {
  createRequestKey,
  formatVnd,
  parseVndInput,
  paymentApi,
  paymentLink,
  PAYMENT_STATUS,
} from "../utils/paymentRequests";

const primaryButton = "inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--color-primary)] px-4 py-2 font-medium text-white hover:bg-[#805b42] disabled:cursor-not-allowed disabled:opacity-50";
const secondaryButton = "inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50";
const inputClass = "mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 focus:border-[var(--color-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)] disabled:bg-gray-100";
const blankForm = () => ({ customerName: "", amount: "", transferContent: "" });

function StatusBadge({ status }) {
  const colors = status === "received"
    ? "bg-green-50 text-green-800"
    : status === "cancelled"
      ? "bg-gray-100 text-gray-700"
      : "bg-amber-50 text-amber-900";
  return <span className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${colors}`}>{PAYMENT_STATUS[status] || status}</span>;
}

function dateLabel(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString("vi-VN");
}

function LinkActions({ item }) {
  if (item.status === "cancelled") return null;
  const link = paymentLink(item.id);
  return <>
    <CopyButton value={link} label="Sao chép link" className={secondaryButton} />
    <a className={secondaryButton} href={link} target="_blank" rel="noopener noreferrer">
      <ExternalLink size={16} aria-hidden="true" /> Mở trang khách hàng
    </a>
  </>;
}

export default function PaymentRequests({ active = true, token, onUnauthorized }) {
  const [view, setView] = useState("list");
  const [list, setList] = useState({ items: [], total: 0, page: 1, limit: 20 });
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState({ search: "", page: 1 });
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [reload, setReload] = useState(0);
  const [form, setForm] = useState(blankForm);
  const [bank, setBank] = useState(null);
  const [bankLoading, setBankLoading] = useState(false);
  const [bankError, setBankError] = useState("");
  const [settingsReload, setSettingsReload] = useState(0);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [selected, setSelected] = useState(null);
  const [created, setCreated] = useState(false);
  const [statusError, setStatusError] = useState("");
  const [statusLoading, setStatusLoading] = useState(false);
  const submitLock = useRef(false);
  const statusLock = useRef(false);
  const request = useRef(null);
  const loadedListKey = useRef("");
  const amountVnd = parseVndInput(form.amount);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilter((current) => current.search === search.trim() ? current : { search: search.trim(), page: 1 });
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const listKey = `${filter.search}:${filter.page}:${reload}`;
    if (!active || view !== "list" || loadedListKey.current === listKey) return undefined;
    const controller = new AbortController();
    setListLoading(true);
    setListError("");
    paymentApi.list(token, filter, controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) {
          loadedListKey.current = listKey;
          setList(data);
        }
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        if (error.status === 401) onUnauthorized?.();
        setListError(error.message || "Không tải được danh sách phiếu cọc. Vui lòng thử lại.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setListLoading(false);
      });
    return () => controller.abort();
  }, [active, token, filter, reload, view, onUnauthorized]);

  useEffect(() => {
    if (!active || view !== "create" || bank) return undefined;
    const controller = new AbortController();
    setBank(null);
    setBankLoading(true);
    setBankError("");
    paymentApi.settings(token, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        if (!data.bank?.bankName || !data.bank?.accountName || !data.bank?.accountNumber) {
          throw new Error("Tài khoản nhận tiền chưa được cấu hình đầy đủ.");
        }
        setBank(data.bank);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        if (error.status === 401) onUnauthorized?.();
        setBankError(error.message || "Không tải được tài khoản nhận tiền. Vui lòng thử lại.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setBankLoading(false);
      });
    return () => controller.abort();
  }, [active, token, view, bank, settingsReload, onUnauthorized]);

  function startCreate() {
    request.current = null;
    setForm(blankForm());
    setFormError("");
    setCreated(false);
    setView("create");
  }

  function openDetail(item) {
    setSelected(item);
    setCreated(false);
    setStatusError("");
    setView("detail");
  }

  async function submit(event) {
    event.preventDefault();
    if (submitLock.current) return;
    if (!form.customerName.trim() || form.customerName.length > 200) {
      setFormError("Vui lòng nhập tên khách hàng, tối đa 200 ký tự.");
      return;
    }
    if (amountVnd === null) {
      setFormError("Số tiền phải là số nguyên VND lớn hơn 0, tối đa 999.999.999.999đ.");
      return;
    }
    if (!form.transferContent.trim() || form.transferContent.length > 500) {
      setFormError("Vui lòng nhập nội dung chuyển khoản, tối đa 500 ký tự.");
      return;
    }
    if (!bank) {
      setFormError("Vui lòng tải tài khoản nhận tiền trước khi tạo phiếu.");
      return;
    }
    const payload = {
      customerName: form.customerName.trim(),
      amountVnd,
      transferContent: form.transferContent,
    };
    submitLock.current = true;
    setSubmitting(true);
    setFormError("");
    try {
      const signature = JSON.stringify(payload);
      if (request.current?.signature !== signature) {
        request.current = { signature, key: createRequestKey() };
      }
      const item = await paymentApi.create(token, payload, request.current.key);
      setSelected(item);
      setCreated(true);
      setStatusError("");
      setReload((value) => value + 1);
      setView("detail");
    } catch (error) {
      if (error.status === 401) onUnauthorized?.();
      setFormError(error.message || "Chưa nhận được kết quả tạo phiếu. Hãy thử lại với cùng thông tin để tránh trùng phiếu.");
    } finally {
      submitLock.current = false;
      setSubmitting(false);
    }
  }

  async function updateStatus(status) {
    if (statusLock.current || selected?.status !== "pending") return;
    const message = status === "received"
      ? "Bạn đã kiểm tra tài khoản ngân hàng và xác nhận nhận đủ tiền cọc của phiếu này?"
      : "Hủy phiếu cọc này? Khách sẽ không thể xem thông tin thanh toán qua link đã gửi.";
    if (!window.confirm(message)) return;
    statusLock.current = true;
    setStatusLoading(true);
    setStatusError("");
    try {
      const updated = await paymentApi.updateStatus(token, selected.id, status);
      setSelected(updated);
      setReload((value) => value + 1);
    } catch (error) {
      if (error.status === 401) onUnauthorized?.();
      setStatusError(error.message || "Không cập nhật được trạng thái. Vui lòng thử lại.");
    } finally {
      statusLock.current = false;
      setStatusLoading(false);
    }
  }

  if (view === "create") {
    return (
      <section aria-labelledby="payment-create-heading" className="mx-auto max-w-2xl">
        <h2 id="payment-create-heading" className="mb-5 text-2xl font-bold text-[var(--color-primary)]">Tạo phiếu cọc</h2>
        <form onSubmit={submit} noValidate className="space-y-5 rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
          <div>
            <label htmlFor="payment-customer" className="font-medium">Tên khách hàng</label>
            <input id="payment-customer" className={inputClass} value={form.customerName} maxLength={200} autoComplete="name" required disabled={submitting} onChange={(event) => setForm({ ...form, customerName: event.target.value })} />
          </div>
          <div>
            <label htmlFor="payment-amount" className="font-medium">Số tiền cọc (VND)</label>
            <input id="payment-amount" className={inputClass} value={form.amount} inputMode="numeric" placeholder="Ví dụ: 20.780.000" aria-describedby="payment-amount-preview" required disabled={submitting} onChange={(event) => setForm({ ...form, amount: event.target.value })} />
            <p id="payment-amount-preview" className="mt-2 text-sm text-gray-600" aria-live="polite">{amountVnd !== null ? formatVnd(amountVnd) : "Nhập số nguyên lớn hơn 0. Có thể dùng dấu chấm phân cách hàng nghìn."}</p>
          </div>
          <div>
            <label htmlFor="payment-content" className="font-medium">Nội dung chuyển khoản</label>
            <textarea id="payment-content" className={inputClass} value={form.transferContent} rows={3} maxLength={500} required disabled={submitting} onChange={(event) => setForm({ ...form, transferContent: event.target.value })} />
            <p className="mt-1 text-sm text-gray-500">Nội dung được lưu đúng như bạn nhập. Tối đa 500 ký tự.</p>
          </div>
          <div className="rounded-lg bg-[#faf7f3] p-4">
            <h3 className="font-semibold">Tài khoản nhận tiền</h3>
            {bankLoading && <p role="status" className="mt-2 text-sm">Đang tải tài khoản nhận tiền...</p>}
            {bankError && <div className="mt-2"><p role="alert" className="text-sm text-red-700">{bankError}</p><button type="button" className={`${secondaryButton} mt-3`} onClick={() => setSettingsReload((value) => value + 1)}>Tải lại tài khoản</button></div>}
            {bank && <dl className="mt-3 grid gap-2 text-sm"><div><dt className="inline text-gray-600">Ngân hàng: </dt><dd className="inline font-medium">{bank.bankName}</dd></div><div><dt className="inline text-gray-600">Tên tài khoản: </dt><dd className="inline font-medium">{bank.accountName}</dd></div><div><dt className="inline text-gray-600">Số tài khoản: </dt><dd className="inline font-medium">{bank.accountNumber}</dd></div></dl>}
          </div>
          {formError && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{formError}</p>}
          <div className="flex flex-col gap-3 sm:flex-row">
            <button type="submit" disabled={submitting || bankLoading || !bank} className={primaryButton}>{submitting ? "Đang tạo phiếu..." : "Tạo phiếu cọc"}</button>
            <button type="button" disabled={submitting} onClick={() => setView("list")} className={secondaryButton}>Hủy / quay lại</button>
          </div>
        </form>
      </section>
    );
  }

  if (view === "detail" && selected) {
    return (
      <section aria-labelledby="payment-detail-heading" className="mx-auto max-w-2xl">
        <button type="button" className={`${secondaryButton} mb-5`} disabled={statusLoading} onClick={() => setView("list")}><ArrowLeft size={16} aria-hidden="true" /> Quay lại danh sách phiếu cọc</button>
        {created && <p role="status" className="mb-4 rounded-lg bg-green-50 p-3 text-green-800">Đã tạo phiếu cọc thành công. Bạn có thể sao chép link để gửi khách hàng.</p>}
        <div className="space-y-5 rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3"><h2 id="payment-detail-heading" className="text-xl font-bold text-[var(--color-primary)]">Phiếu cọc {selected.code}</h2><StatusBadge status={selected.status} /></div>
          <dl className="space-y-4">
            <div><dt className="text-sm text-gray-500">Tên khách hàng</dt><dd className="break-words font-medium">{selected.customerName}</dd></div>
            <div><dt className="text-sm text-gray-500">Số tiền cọc</dt><dd className="text-3xl font-bold text-[var(--color-primary)]">{formatVnd(selected.amountVnd)}</dd></div>
            <div><dt className="text-sm text-gray-500">Nội dung chuyển khoản</dt><dd className="whitespace-pre-wrap break-words">{selected.transferContent}</dd></div>
            <div><dt className="text-sm text-gray-500">Tài khoản nhận tiền</dt><dd>{selected.bank.bankName} · {selected.bank.accountNumber}<br />{selected.bank.accountName}</dd></div>
            <div><dt className="text-sm text-gray-500">Ngày tạo</dt><dd>{dateLabel(selected.createdAt)}</dd></div>
          </dl>
          {selected.status !== "cancelled" ? <div className="space-y-3 border-t border-gray-100 pt-5"><label htmlFor="payment-share-link" className="block text-sm font-medium">Link thanh toán dành cho khách</label><input id="payment-share-link" className={inputClass} value={paymentLink(selected.id)} readOnly onFocus={(event) => event.target.select()} /><div className="flex flex-wrap gap-2"><LinkActions item={selected} /></div></div> : <p className="rounded-lg bg-gray-100 p-3 text-sm text-gray-700">Phiếu đã hủy. Link thanh toán không còn khả dụng.</p>}
          {selected.status === "pending" && <div className="space-y-3 border-t border-gray-100 pt-5"><p className="text-sm text-gray-600">Chỉ xác nhận đã nhận cọc sau khi kiểm tra tiền thực nhận trong tài khoản ngân hàng.</p><div className="flex flex-wrap gap-3"><button type="button" className={primaryButton} disabled={statusLoading} onClick={() => updateStatus("received")}>Xác nhận đã nhận cọc</button><button type="button" className={`${secondaryButton} text-red-700`} disabled={statusLoading} onClick={() => updateStatus("cancelled")}>Hủy phiếu cọc</button></div></div>}
          {statusLoading && <p role="status" className="text-sm text-gray-600">Đang cập nhật trạng thái...</p>}
          {statusError && <p role="alert" className="text-sm text-red-700">{statusError}</p>}
        </div>
      </section>
    );
  }

  const pages = Math.max(1, Math.ceil(list.total / list.limit));
  return (
    <section aria-labelledby="payment-list-heading">
      <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div><h2 id="payment-list-heading" className="text-2xl font-bold text-[var(--color-primary)]">Phiếu cọc</h2><p className="mt-1 text-sm text-gray-600">Tạo phiếu và gửi link thanh toán cho khách hàng.</p></div>
        <button type="button" onClick={startCreate} className={primaryButton}><Plus size={18} aria-hidden="true" /> Tạo phiếu cọc</button>
      </div>
      <label htmlFor="payment-search" className="text-sm font-medium">Tìm phiếu cọc</label>
      <div className="relative mb-5"><Search size={18} aria-hidden="true" className="absolute left-3 top-5 text-gray-400" /><input id="payment-search" className={`${inputClass} pl-10`} value={search} maxLength={200} placeholder="Tên khách hàng, mã phiếu hoặc nội dung chuyển khoản" onChange={(event) => setSearch(event.target.value)} /></div>
      {listLoading ? <p role="status" className="rounded-xl border border-gray-200 p-8 text-center text-gray-600">Đang tải phiếu cọc...</p> : listError ? <div className="rounded-xl border border-red-200 p-5"><p role="alert" className="text-red-700">{listError}</p><button type="button" className={`${secondaryButton} mt-3`} onClick={() => setReload((value) => value + 1)}>Thử lại</button></div> : list.items.length === 0 ? <p role="status" className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-gray-600">{filter.search ? "Không tìm thấy phiếu cọc phù hợp." : "Chưa có phiếu cọc. Tạo phiếu đầu tiên để gửi khách hàng."}</p> : <>
        <div className="space-y-4">{list.items.map((item) => <article key={item.id} aria-label={`Phiếu cọc ${item.code}`} className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
          <div className="mb-3 flex flex-wrap items-start justify-between gap-3"><div><h3 className="font-bold text-[var(--color-primary)]">{item.code}</h3><p className="mt-1 break-words font-medium">{item.customerName}</p></div><StatusBadge status={item.status} /></div>
          <dl className="grid min-w-0 gap-3 text-sm sm:grid-cols-2"><div className="min-w-0 sm:col-span-2"><dt className="text-gray-500">Nội dung chuyển khoản</dt><dd className="whitespace-pre-wrap break-words">{item.transferContent}</dd></div><div><dt className="text-gray-500">Số tiền cọc</dt><dd className="text-lg font-bold">{formatVnd(item.amountVnd)}</dd></div><div><dt className="text-gray-500">Ngân hàng nhận tiền</dt><dd>{item.bank.bankName}</dd></div><div className="sm:col-span-2"><dt className="text-gray-500">Ngày tạo</dt><dd>{dateLabel(item.createdAt)}</dd></div></dl>
          <div className="mt-4 flex flex-wrap gap-2"><button type="button" className={secondaryButton} onClick={() => openDetail(item)}>Xem phiếu</button><LinkActions item={item} /></div>
        </article>)}</div>
        <nav aria-label="Phân trang phiếu cọc" className="mt-5 flex flex-wrap items-center justify-between gap-3 text-sm"><span>{list.total} phiếu · Trang {list.page}/{pages}</span><div className="flex gap-2"><button type="button" className={secondaryButton} disabled={list.page <= 1} onClick={() => setFilter((current) => ({ ...current, page: current.page - 1 }))}>Trang trước</button><button type="button" className={secondaryButton} disabled={list.page >= pages} onClick={() => setFilter((current) => ({ ...current, page: current.page + 1 }))}>Trang sau</button></div></nav>
      </>}
    </section>
  );
}
